import React, {
  ButtonHTMLAttributes,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { Images } from '../../assets';
import { LoginWithGoogleReturnPath } from '../../model/user/types/LoginWithGoogleReturnPath.enum';
import { AtiraLinkActions } from '../../redux/atira-link/atira-link.slice';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { userSliceSelectors } from '../../redux/user/user.selector';
import { userActions } from '../../redux/user/user.slice';
import { FontSize } from '../../theme/FontSize';
import { Rounded } from '../../theme/Rounded';
import { Spacing } from '../../theme/Spacing';
import { ThemeType } from '../../theme/Theme';
import { AtiraToast } from '../../utils/AtiraToast';
import { onNavigateWithRedirect } from '../../utils/OnNavigateWithRedirect';
import { Button } from '../Button';
import { AtiraImage } from '../Image';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  color?: keyof ThemeType;
  backgroundColor?: keyof ThemeType;
  padding?: string;
  fontSize?: keyof typeof Spacing;
  borderRadius?: keyof typeof Rounded;
  border?: string;
  children?: React.ReactNode;
  margin?: string;
  width?: string;
  height?: string;
  loading?: boolean;
  dataTestId?: string;
}

// @ts-ignore
const StyledButton: React.FC<ButtonProps> = styled(Button).attrs(
  // @ts-ignore
  (props: StyledButtonProps) => ({
    'data-testid': props.dataTestId,
  }),
)`
  background-color: ${(props) =>
    props.backgroundColor
      ? props.theme[props.backgroundColor]
      : props.theme.white};
  color: ${(props) =>
    props.color ? props.theme[props.color] : props.theme.black};
  padding: ${(props) => props.padding || `${Spacing.s} ${Spacing.l}`};
  border-radius: ${(props) => props.borderRadius || Rounded.lg};
  border: ${(props) => props.border || 'none'};
  font-size: ${(props) => props.fontSize || FontSize.m};
  width: ${(props) => props.width || '100%'};
  height: ${(props) => props.height || '3rem'};
  margin: ${(props) => props.margin || '0'};
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${Spacing.s};
  background-color: #e9f1ff;
  color: #4285f4;

  &:hover {
    background-color: #bed6ff;
  }
`;

interface GoogleButtonProps {
  color?: keyof ThemeType;
  backgroundColor?: keyof ThemeType;
  padding?: string;
  fontSize?: keyof typeof Spacing;
  borderRadius?: keyof typeof Rounded;
  border?: string;
  children?: React.ReactNode;
  margin?: string;
  width?: string;
  height?: string;
  loading?: boolean;
  title?: string;
  returnPath: LoginWithGoogleReturnPath;
  onAfterAuthURLGeneration?: VoidFunction;
  disabled?: boolean;
  dataTestId?: string;
}

export const GoogleButton: React.FC<GoogleButtonProps> = ({
  disabled,
  dataTestId,
  ...props
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const loggedInUserId = useAppSelector(
    userSliceSelectors.selectLoggedInUserId,
  );

  const googleAuth = async () => {
    setIsLoading(true);
    try {
      const googleAuthUrl = await dispatch(
        userActions.createLoginWithGoogleLink({ returnPath: props.returnPath }),
      ).unwrap();
      if (props.onAfterAuthURLGeneration) {
        props.onAfterAuthURLGeneration();
      }

      window.open(googleAuthUrl, '_self');
    } catch (error: any) {
      console.log(error);
      AtiraToast.error(t('common.wrong_contact_support'));
      setIsLoading(false);
    }
  };

  const verifyAndLogUser = useCallback(
    async (code: string) => {
      try {
        const { user } = await dispatch(
          userActions.loginWithGoogle({ code, returnPath: props.returnPath }),
        ).unwrap();

        if (user) {
          await dispatch(
            AtiraLinkActions.getUserAtiraLinks({ userId: user._id }),
          ).unwrap();
          if (props.returnPath === LoginWithGoogleReturnPath.PRICING) {
          } else {
            onNavigateWithRedirect(navigate);
          }
        }
      } catch (error) {
        console.log(error);
        AtiraToast.error(t('common.wrong_contact_support'));
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, props.returnPath, navigate, t],
  );

  useEffect(() => {
    const verificationCode = searchParams.get('code');
    if (verificationCode && !loggedInUserId) {
      setIsLoading(true);
      searchParams.set('code', '');
      verifyAndLogUser(verificationCode);
    }
  }, [searchParams, verifyAndLogUser, loggedInUserId]);

  return (
    <StyledButton
      disabled={disabled === true}
      loading={isLoading}
      onClick={googleAuth}
      dataTestId={dataTestId}
      {...props}
    >
      <AtiraImage src={Images.GoogleIcon} width="1.5rem" />
      {props.children}
    </StyledButton>
  );
};
